import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertTriangleIcon,
  ListPlusIcon,
  Loader2Icon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react'

import {
  createFlexibleItem,
  deleteFlexibleItem,
  updateFlexibleItem,
  type FlexibleItemRouting,
} from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import { formatNumericInput, parseNumericInput } from '@/lib/currency'
import {
  TREATMENTS,
  TREATMENT_MEANINGS,
  normalizeTreatment,
  yearOf,
  type FlexibleLineItem,
  type Treatment,
} from '@/lib/flexibleLineItems'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * FIELDS-2 — custom statement line items, and the treatment that routes them.
 *
 * The P&L grid holds seven fields. Real statements do not: "Gain on sale of
 * equipment", "PPP loan forgiveness", "Owner's country-club dues", "Accrued
 * PTO". Before this, anything the grid had no row for had to be either forced
 * into a field where it did not belong or dropped — and the extractor's
 * unmapped lines lived only inside a run report that scrolled away.
 *
 * TREATMENT IS THE POINT. "Misc. other income, 84,000" tells a calculator
 * nothing; the treatment is what says where the money goes. So it is required
 * on the add form and the API refuses a save without one (422). An item with no
 * treatment — every extracted line starts that way — is counted NOWHERE: not as
 * zero, not as revenue, not as an add-back. It sits in the review queue below
 * until somebody classifies or deletes it. An unclassified figure is an open
 * question, and the honest thing is to leave it out and keep asking.
 *
 * Routing itself lives in @/lib/flexibleLineItems (mirroring
 * core/flexible_line_items.py); nothing here does arithmetic.
 */

/** Short option text. The full sentence lives in TREATMENT_MEANINGS below the select. */
const TREATMENT_LABELS: Record<Treatment, string> = {
  revenue: 'Revenue',
  opex: 'Operating expense',
  add_back: 'Add-back (non-owner)',
  owner_benefit: 'Owner benefit (SDE only)',
  debt_like: 'Debt-like (equity bridge)',
  memo: 'Memo (excluded from calcs)',
}

const SELECT_CLASS =
  'h-7 rounded border border-input bg-transparent px-1 text-xs outline-none focus:ring-1 focus:ring-ring'

const fallbackCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    .format(value)

// ---------------------------------------------------------------------------
// Inline "+ Add line item" affordance — one per statement section
// ---------------------------------------------------------------------------

interface AddProps {
  dealId: string
  /** The fiscal year the item attaches to. A deal-level item (debt-like) still
   *  carries one; the bridge ignores year, the P&L honours it. */
  year: number
  /** Pre-selected treatment for the section this sits under. Still changeable —
   *  a line under "Revenue" can legitimately be a memo. */
  defaultTreatment: Treatment
  onCreated: () => void
}

export function AddLineItemInline({ dealId, year, defaultTreatment, onCreated }: AddProps) {
  const [open, setOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [amount, setAmount] = useState('')
  const [treatment, setTreatment] = useState<Treatment>(defaultTreatment)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setLabel('')
    setAmount('')
    setNote('')
    setTreatment(defaultTreatment)
  }

  const submit = async () => {
    const trimmed = label.trim()
    if (!trimmed) {
      // Matched to the API's own 422 wording so the two never contradict.
      toast.error('Give the line item a label before saving it.')
      return
    }
    const parsed = parseNumericInput(amount)
    setSaving(true)
    try {
      await createFlexibleItem(dealId, {
        label: trimmed,
        amount: parsed === '' ? 0 : parsed,
        year,
        treatment,
        note: note.trim() || null,
      })
      toast.success(`Added "${trimmed}" to FY${year}.`)
      reset()
      setOpen(false)
      onCreated()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setSaving(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 self-start rounded px-1 py-0.5 text-[11px] text-muted-foreground transition-colors hover:text-brand-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <PlusIcon className="h-3 w-3" /> Add line item
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border bg-muted/30 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="Line item label"
          aria-label="Line item label"
          className="h-7 min-w-40 flex-1 text-xs"
        />
        <Input
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={e => setAmount(formatNumericInput(e.target.value))}
          placeholder="Amount"
          aria-label="Line item amount"
          className="h-7 w-28 text-right text-xs tabular-nums"
        />
        <select
          value={treatment}
          onChange={e => setTreatment(e.target.value as Treatment)}
          aria-label="Treatment"
          className={`${SELECT_CLASS} w-48`}
        >
          {TREATMENTS.map(t => (
            <option key={t} value={t}>{TREATMENT_LABELS[t]}</option>
          ))}
        </select>
      </div>
      <Input
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="Note (optional) — where the figure came from, why it belongs here"
        aria-label="Line item note"
        className="h-7 text-xs"
      />
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        {TREATMENT_MEANINGS[treatment]}
      </p>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={saving} className="h-7 gap-1.5 text-xs">
          {saving && <Loader2Icon className="h-3.5 w-3.5 animate-spin" />}
          Add to FY{year}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => { reset(); setOpen(false) }}
          disabled={saving}
          className="h-7 text-xs"
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The register — every custom item, plus the treatment review queue
// ---------------------------------------------------------------------------

interface PanelProps {
  dealId: string
  items: FlexibleLineItem[]
  routing?: FlexibleItemRouting | null
  /** treatment -> meaning, as the server sent it. Falls back to the local copy. */
  treatments?: Record<string, string> | null
  loading?: boolean
  years: readonly number[]
  onChanged: () => void
  fmtCurrency?: (value: number) => string
}

export function FlexibleLineItemsPanel({
  dealId,
  items,
  routing,
  treatments,
  loading = false,
  years,
  onChanged,
  fmtCurrency = fallbackCurrency,
}: PanelProps) {
  const [busy, setBusy] = useState<Record<string, boolean>>({})

  const { queue, classified } = useMemo(() => {
    const q: FlexibleLineItem[] = []
    const c: FlexibleLineItem[] = []
    for (const item of items) {
      if (normalizeTreatment(item.treatment) === null) q.push(item)
      else c.push(item)
    }
    // Grouped by treatment so the register reads like a schedule, not a log.
    c.sort((a, b) => {
      const ai = TREATMENTS.indexOf(normalizeTreatment(a.treatment) as Treatment)
      const bi = TREATMENTS.indexOf(normalizeTreatment(b.treatment) as Treatment)
      if (ai !== bi) return ai - bi
      return (yearOf(a.year) ?? 0) - (yearOf(b.year) ?? 0)
    })
    return { queue: q, classified: c }
  }, [items])

  const mutate = async (
    itemId: string,
    run: () => Promise<unknown>,
    success: string,
  ) => {
    setBusy(prev => ({ ...prev, [itemId]: true }))
    try {
      await run()
      toast.success(success)
      onChanged()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setBusy(prev => {
        const { [itemId]: _drop, ...rest } = prev
        return rest
      })
    }
  }

  const assignTreatment = (item: FlexibleLineItem, next: string) => {
    const treatment = normalizeTreatment(next)
    if (!treatment || !item.item_id) return
    const id = item.item_id
    void mutate(
      id,
      () => updateFlexibleItem(dealId, id, { treatment }),
      `"${item.label}" is now ${TREATMENT_LABELS[treatment].toLowerCase()}.`,
    )
  }

  const assignYear = (item: FlexibleLineItem, next: string) => {
    if (!item.item_id) return
    const id = item.item_id
    const year = next === '' ? null : Number(next)
    void mutate(
      id,
      () => updateFlexibleItem(dealId, id, { year }),
      year === null ? 'Year cleared.' : `Moved to FY${year}.`,
    )
  }

  const remove = (item: FlexibleLineItem) => {
    if (!item.item_id) return
    const id = item.item_id
    void mutate(id, () => deleteFlexibleItem(dealId, id), `Removed "${item.label}".`)
  }

  const meaning = (treatment: Treatment) =>
    treatments?.[treatment] ?? TREATMENT_MEANINGS[treatment]

  const treatmentSelect = (item: FlexibleLineItem, placeholder: boolean) => {
    const current = normalizeTreatment(item.treatment)
    return (
      <select
        value={current ?? ''}
        onChange={e => assignTreatment(item, e.target.value)}
        disabled={!!busy[item.item_id ?? '']}
        aria-label={`Treatment for ${item.label}`}
        className={`${SELECT_CLASS} w-48`}
      >
        {placeholder && <option value="">— choose a treatment —</option>}
        {TREATMENTS.map(t => (
          <option key={t} value={t}>{TREATMENT_LABELS[t]}</option>
        ))}
      </select>
    )
  }

  const yearSelect = (item: FlexibleLineItem) => (
    <select
      value={yearOf(item.year) ?? ''}
      onChange={e => assignYear(item, e.target.value)}
      disabled={!!busy[item.item_id ?? '']}
      aria-label={`Fiscal year for ${item.label}`}
      className={`${SELECT_CLASS} w-24`}
    >
      <option value="">No year</option>
      {years.map(y => <option key={y} value={y}>FY{y}</option>)}
    </select>
  )

  const provenanceLine = (item: FlexibleLineItem): string | null => {
    if (item.source !== 'extracted') return item.note?.trim() || null
    const p = (item.provenance ?? {}) as Record<string, unknown>
    const filename = typeof p.filename === 'string' ? p.filename : null
    const sourceLabel = typeof p.source_label === 'string' ? p.source_label : null
    const locator = typeof p.locator === 'string' ? p.locator : null
    const bits = [sourceLabel, filename, locator].filter(Boolean)
    return bits.length ? `Extracted from ${bits.join(' · ')}` : 'Extracted — source unknown'
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3">
      <div className="flex items-center gap-2">
        <ListPlusIcon className="h-4 w-4 text-brand-primary" />
        <h2 className="text-sm font-semibold text-brand-text">Custom Line Items</h2>
        {queue.length > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            {queue.length} awaiting treatment
          </span>
        )}
      </div>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Anything the seven-field P&amp;L grid has no row for. Each item&apos;s{' '}
        <strong>treatment</strong> decides where it lands — revenue, operating
        expense, add-back, owner benefit, the equity bridge, or memo-only. An item
        with no treatment is counted nowhere at all, in any figure or report.
      </p>

      {loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-full" />
          <Skeleton className="h-7 w-full" />
        </div>
      ) : (
        <>
          {queue.length > 0 && (
            <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2 dark:border-amber-800 dark:bg-amber-950/40">
              <div className="flex items-center gap-1.5">
                <AlertTriangleIcon className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
                <span className="text-xs font-medium text-amber-900 dark:text-amber-200">
                  Needs a treatment — counted nowhere until classified
                </span>
              </div>
              {queue.map(item => (
                <div
                  key={item.item_id}
                  className="flex flex-wrap items-center gap-2 border-t border-amber-200 pt-2 first:border-t-0 first:pt-0 dark:border-amber-900"
                >
                  <div className="min-w-40 flex-1">
                    <p className="text-xs font-medium text-brand-text">{item.label}</p>
                    {provenanceLine(item) && (
                      <p className="text-[11px] text-muted-foreground">{provenanceLine(item)}</p>
                    )}
                  </div>
                  <span className="w-28 text-right text-xs tabular-nums text-brand-text">
                    {fmtCurrency(Number(item.amount) || 0)}
                  </span>
                  {yearSelect(item)}
                  {treatmentSelect(item, true)}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(item)}
                    disabled={!!busy[item.item_id ?? '']}
                    aria-label={`Delete ${item.label}`}
                    className="h-7 w-7 p-0"
                  >
                    <Trash2Icon className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {classified.length === 0 && queue.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No custom line items yet. Use &ldquo;Add line item&rdquo; under any
              statement section above.
            </p>
          )}

          {classified.length > 0 && (
            <div className="flex flex-col divide-y divide-border">
              {classified.map(item => {
                const treatment = normalizeTreatment(item.treatment) as Treatment
                return (
                  <div key={item.item_id} className="flex flex-wrap items-center gap-2 py-2">
                    <div className="min-w-40 flex-1">
                      <p className="text-xs font-medium text-brand-text">{item.label}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {provenanceLine(item) ?? meaning(treatment)}
                      </p>
                    </div>
                    <span className="w-28 text-right text-xs tabular-nums text-brand-text">
                      {fmtCurrency(Number(item.amount) || 0)}
                    </span>
                    {yearSelect(item)}
                    {treatmentSelect(item, false)}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(item)}
                      disabled={!!busy[item.item_id ?? '']}
                      aria-label={`Delete ${item.label}`}
                      className="h-7 w-7 p-0"
                    >
                      <Trash2Icon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )
              })}
            </div>
          )}

          {routing && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-2 text-[11px] text-muted-foreground">
              {/* Deal-level totals across every year, which is why they will not
                  tie to the selected year's KPIs on their own. */}
              <span>Revenue {fmtCurrency(routing.revenue_delta)}</span>
              <span>Opex {fmtCurrency(routing.opex_delta)}</span>
              <span>Add-backs {fmtCurrency(routing.add_back_total)}</span>
              <span>Owner benefit {fmtCurrency(routing.owner_benefit_total)}</span>
              <span>Debt-like {fmtCurrency(routing.debt_like_total)}</span>
              <span>{routing.memo_count} memo</span>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default FlexibleLineItemsPanel
